import { create } from 'zustand';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

interface PWAState {
  installPrompt: BeforeInstallPromptEvent | null;
  isInstalled: boolean;
  canInstall: boolean;
  isOffline: boolean;
  updateAvailable: boolean;
  swRegistration: ServiceWorkerRegistration | null;
  isMobileSidebarOpen: boolean;

  setInstallPrompt: (prompt: BeforeInstallPromptEvent | null) => void;
  setIsInstalled: (installed: boolean) => void;
  setCanInstall: (canInstall: boolean) => void;
  setIsOffline: (offline: boolean) => void;
  setUpdateAvailable: (available: boolean, registration?: ServiceWorkerRegistration) => void;
  setMobileSidebarOpen: (open: boolean) => void;
  toggleMobileSidebar: () => void;
  promptInstall: () => Promise<boolean>;
  applyUpdate: () => void;
}

export const usePWAStore = create<PWAState>((set, get) => ({
  installPrompt: null,
  isInstalled: false,
  canInstall: false,
  isOffline: !navigator.onLine,
  updateAvailable: false,
  swRegistration: null,
  isMobileSidebarOpen: false,

  setInstallPrompt: (prompt) => set({ installPrompt: prompt, canInstall: !!prompt }),
  setIsInstalled: (installed) => set({ isInstalled: installed, canInstall: !installed }),
  setCanInstall: (canInstall) => set({ canInstall }),
  setIsOffline: (offline) => set({ isOffline: offline }),
  setUpdateAvailable: (available, registration) =>
    set({ updateAvailable: available, swRegistration: registration || null }),
  setMobileSidebarOpen: (open) => set({ isMobileSidebarOpen: open }),
  toggleMobileSidebar: () => set((state) => ({ isMobileSidebarOpen: !state.isMobileSidebarOpen })),

  promptInstall: async () => {
    const { installPrompt } = get();
    if (!installPrompt) return false;

    try {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;

      if (outcome === 'accepted') {
        set({ isInstalled: true, canInstall: false, installPrompt: null });
        return true;
      }
    } catch (error) {
      console.error('Installation prompt error:', error);
    }

    return false;
  },

  applyUpdate: () => {
    const { swRegistration } = get();
    if (swRegistration?.waiting) {
      swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
      window.location.reload();
    }
  },
}));

if (typeof window !== 'undefined') {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    usePWAStore.getState().setInstallPrompt(e as BeforeInstallPromptEvent);
  });

  window.addEventListener('appinstalled', () => {
    usePWAStore.getState().setIsInstalled(true);
    usePWAStore.getState().setInstallPrompt(null);
  });

  if (window.matchMedia('(display-mode: standalone)').matches) {
    usePWAStore.getState().setIsInstalled(true);
  }

  if ((window.navigator as any).standalone === true) {
    usePWAStore.getState().setIsInstalled(true);
  }
}
